import { XMarkIcon, FireIcon } from '@heroicons/react/20/solid'
import { useState } from 'react'
import { ethers, BigNumber } from 'ethers'
import { useAccount, useSigner, useNetwork } from 'wagmi'

import { CircularProgress } from '../../components'
import { ButtonPill } from '../../components'

import { Points, Reward } from '../../types'
import { API_KEY } from '../../constants'

interface Props {
  paymentID?: string
  points?: Points
  rewards?: Reward[]
  onClose: () => void
}

export default function Rewards({
  points,
  onClose,
  rewards = [],
  paymentID,
}: Props) {
  const [isBurning, setIsBurning] = useState(false)
  const [burningId, setBurningId] = useState('')

  const { chain } = useNetwork()
  const { address, isConnected } = useAccount()
  const { data: signerWalletConnect, isError, isLoading } = useSigner()

  async function connectedWalletInfo() {
    try {
      if (isConnected) {
        if (!address) throw new Error('no wallet connect address')
        if (!signerWalletConnect) throw new Error('no signerWalletConnect :(')

        return {
          address,
          chainId: `0x${chain?.id.toString(16)}`,
          signer: signerWalletConnect,
        }
      } else {
        if (!(window as any).ethereum) throw new Error('no window.ethereum')

        const provider = new ethers.providers.Web3Provider(
          (window as any).ethereum,
          'any',
        )

        if (!provider) {
          throw new Error('connect wallet webview - no provider')
        }
        const signer = provider.getSigner()
        if (!signer) throw new Error('no signer :(')

        const accounts = await (window as any).ethereum.request({
          method: 'eth_requestAccounts',
        })
        if (!accounts[0]) throw new Error('connect wallet webview - no account')

        const chainId = (await (window as any).ethereum.request({
          method: 'eth_chainId',
        })) as string

        if (!chainId) throw new Error('no chain_id')

        return {
          address: accounts[0],
          chainId,
          signer,
        }
      }
    } catch (err) {
      throw new Error('getting connected wallet info')
    }
  }

  async function handleBurn(reward: Reward) {
    setBurningId(reward.id)

    try {
      const { address, chainId, signer } = await connectedWalletInfo()

      if (!signer) throw new Error('no signerWalletConnect :(')
      if (!chainId) throw new Error('no chain?.id')

      setIsBurning(true)

      const tx = await signer.sendTransaction(reward.burn_tx)

      // setStatus(Status.LOADING)

      const res = await fetch(
        `https://payments-api-c560.onrender.com/api/v1/payment_requests/${paymentID}/rewards`,
        {
          method: 'POST',
          headers: {
            accept: 'application/json',
            'X-API-KEY': API_KEY,
          },
          body: JSON.stringify({
            reward_id: reward.id,
            chain_id: BigNumber.from(chainId).toNumber(),
            points_burn_tx: tx,
          }),
        },
      )

      if (!res.ok) throw new Error('burning coupon failed to fetch')

      await tx.wait()

      //  setStatus(Status.READY)
      setBurningId(reward.id)
      setIsBurning(false)
      onClose()

      //
    } catch (err) {
      console.error(err)

      setBurningId(reward.id)
      setIsBurning(false)

      // setStatus(Status.READY)
    }
  }

  return (
    <div className="h-screen md:max-w-2xl md:mx-auto p-4 flex flex-col">
      <div className="width-full flex flex-col justify-center items-center mt-5 mb-5">
        <h3 className="text-3xl">
          {((points?.value ?? 0) / 10 ** 6).toFixed(0)}
        </h3>
        <p className="mt-2">Points</p>
      </div>

      {rewards.map((rwd) => (
        <div key={rwd.id} className="flex flex-row justify-between mt-10">
          <div className="flex flex-col md:flex-row">
            <img alt="" className="w-20 mr-4" src={rwd.image} />

            <div className="flex flex-col">
              <p>{rwd.label}</p>
              <p className="text-gray-200">{rwd.points_required} points</p>
            </div>
          </div>

          <div className="flex flex-col justify-center">
            <ButtonPill
              disabled={
                (points?.value ?? 0) / 10 ** (points?.decimals ?? 0) <
                rwd.points_required
              }
              onClick={() => handleBurn(rwd)}
              // className="flex justify-center items-center"
            >
              {/* <CircularProgress progress={76} />*/}
              {(points?.value ?? 0) / 10 ** (points?.decimals ?? 0) <
              rwd.points_required ? (
                <p className="mr-2">Not enough points </p>
              ) : (
                <p className="">Redeem </p>
              )}
              {/*<FireIcon className="h-6 w-6 text-gray-300" />*/}
              {isBurning && burningId === rwd.id && (
                <svg
                  aria-hidden="true"
                  role="status"
                  className="inline w-4 h-4 ml-3 text-gray-200 animate-spin dark:text-gray-600"
                  viewBox="0 0 100 101"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="currentColor"
                  />
                  <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="#1C64F2"
                  />
                </svg>
              )}
            </ButtonPill>
          </div>
        </div>
      ))}

      <button className="fixed top-5 right-5" onClick={onClose}>
        <XMarkIcon className="h-8 w-8 text-gray-300" />
      </button>
    </div>
  )
}
